import React, { useEffect } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import AOS from "aos";

interface TestimonialProps {
  name: string;
  timeAgo: string;
  rating: number;
  review: string;
  initial: string;
}

const testimonialData: TestimonialProps[] = [
  {
    name: "Jessica",
    timeAgo: "1 year ago",
    rating: 5,
    review:
      "Great app! Questions are based directly from the textbooks so you know it's the right content, and super easy to use on the go for opportunistic study. I was preparing with GetThru for 2,5 month. Would use again!",
    initial: "E"
  },
  {
    name: "Nicholas",
    timeAgo: "1 year ago",
    rating: 5,
    review:
      "I found get thru an extremely useful tool. The explanations were super helpful for study and I found taught me information in areas which I was lacking. The page references also helped me read around areas I needed to improve on. great resource.",
    initial: "C"
  },
  {
    name: "William",
    timeAgo: "one year ago",
    rating: 5,
    review:
      "Awesome app for GSSE study on the go! Makes revision much more accessbile and would use again to supplement my study.",
    initial: "X"
  },
  {
    name: "Christina",
    timeAgo: "10 months ago",
    rating: 5,
    review:
      "Fantastic app, my most used question bank tool in preparation for the GSSE! Has both the RACS question bank MCQs and also thousands of new MCQs based on content directly from Lasts, Ganongs and Robbins. Question difficulty similar to those from the bank.",
    initial: "P"
  },
  {
    name: "Matias",
    timeAgo: "yesterday",
    rating: 5,
    review:
      "GetThru is an excellent resource for the GSSE. It is easy to use and has a comprehensive, relevant and accurate question bank. Highly recommended!",
    initial: "N"
  }
  // Add other testimonials similarly
];

const ArrowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

function SampleNextArrow(props) {
  const { className, onClick } = props;
  return (
    <ArrowContainer className={className} onClick={onClick}>
      <img src="/assets/home/arrow-right.png" alt="arrow-right" />
    </ArrowContainer>
  );
}

function SamplePrevArrow(props) {
  const { className, onClick } = props;
  return (
    <ArrowContainer className={className} onClick={onClick}>
      <img src="/assets/home/arrow-left.png" alt="arrow-left" />
    </ArrowContainer>
  );
}

const Testimonials: React.FC = () => {
  useEffect(() => {
    // Initialize AOS with settings to only animate once
    AOS.init({
      once: true,
      mirror: false,
      duration: 600,
      offset: 100,
      easing: "ease-out"
    });

    // Refresh AOS on component mount
    AOS.refresh();
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1324,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "40px",
          variableWidth: false
        }
      }
    ],
    centerMode: false,
    variableWidth: false,
    cssEase: "linear",
    autoplaySpeed: 5000
  };

  const getInitialColor = (initial: string) => {
    const colors = {
      J: "#90caf9",
      K: "#80cbc4",
      L: "#b39ddb",
      M: "#ffcc80",
      N: "#a5d6a7"
    };
    return colors[initial as keyof typeof colors] || "#90caf9";
  };

  return (
    <TestimonialsSection>
      <Title data-aos="fade-in" data-aos-duration="500">
        What GetThru Users Say
        <TitleImage src="/assets/home/testimonials.png" alt="testimonials" />
      </Title>
      <div data-aos="fade-in" data-aos-duration="600" data-aos-delay="100">
        <Slider {...settings}>
          {testimonialData.map((testimonial, index) => (
            <TestimonialCard key={index}>
              <UserInfo>
                <UserInitial bgColor={getInitialColor(testimonial.initial)}>
                  {testimonial.initial}
                </UserInitial>
                <div>
                  <UserName>{testimonial.name}</UserName>
                  <TimeAgo>{testimonial.timeAgo}</TimeAgo>
                </div>
              </UserInfo>
              <Rating>
                {"★".repeat(testimonial.rating)}
                {"☆".repeat(5 - testimonial.rating)}
              </Rating>
              <Review>{testimonial.review}</Review>
            </TestimonialCard>
          ))}
        </Slider>
      </div>
      <UserCountBadge
        data-aos="fade-in"
        data-aos-duration="500"
        data-aos-delay="200"
        id="pricing"
      >
        <UserAvatars>
          <UserAvatar src="/assets/home/user1.png" alt="" />
          <UserAvatar src="/assets/home/user2.png" alt="" />
          <UserAvatar src="/assets/home/user3.png" alt="" />
        </UserAvatars>
        <UserCount>+400 users</UserCount>
      </UserCountBadge>
    </TestimonialsSection>
  );
};

const TestimonialsSection = styled.section`
  padding: 40px 0px;
  max-width: 100%;
  margin-left: calc((100vw - 1440px) / 2);
  position: relative;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-left: 0;
  }

  @media (max-width: 1440px) {
    margin-left: 16px;
    width: calc(100% - 32px);
  }

  .slick-slide {
    padding: 10px 10px;
  }

  .slick-list {
    margin: 0 10px;
  }
  .slick-track {
    margin-bottom: 10px;
  }

  .slick-dots li {
    width: 16px;
  }

  .slick-dots li button:before {
    color: #4628ff;
    font-size: 16px;
  }

  .slick-dots li.slick-active button:before {
    color: #4628ff;
    font-size: 16px;
  }

  .slick-prev,
  .slick-next {
    top: -60px;
    width: 36px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    border-color: #4628ff;

    &:hover {
      background: white;
    }
    &::before {
      display: none;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .slick-prev {
    right: 244px;
    left: auto;
  }

  .slick-next {
    right: 200px;
  }

  @media (max-width: 600px) {
    margin-left: 0;
    padding: 20px 0;

    .slick-slide {
      padding: 0 8px;
    }

    .slick-list {
      margin: 0;
      padding: 10px 0;
    }
  }
`;

const Title = styled.h2`
  font-size: 32px;
  margin-bottom: 36px;
  font-weight: 700;
  color: #1c1c1f;
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 24px;
    margin-left: 10px;
  }
`;

const TitleImage = styled.img`
  margin-left: 8px;
  vertical-align: middle;
  transform: translateY(-30px);
  margin-left: 20px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const TestimonialCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 374px;
  display: flex;
  flex-direction: column;

  @media (max-width: 600px) {
    width: 280px;
    height: 320px;
    min-height: unset;
    margin: 0 auto;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  > div:last-child {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1c1c1f;
`;

const TimeAgo = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1c1c1f;
`;

const UserInitial = styled.div<{ bgColor: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => props.bgColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-weight: 500;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const Rating = styled.div`
  color: #ffb800;
  margin-bottom: 12px;
  font-size: 24px;
`;

const Review = styled.p`
  color: #1c1c1f;
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;

  @media (max-width: 600px) {
    -webkit-line-clamp: 5;
  }
`;

const UserCountBadge = styled.div`
  background: white;
  border-radius: 100px;
  padding: 4px 12px;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-left: 16px;
  position: absolute;
  bottom: 10px;
  right: 380px;
  transform: rotate(-8deg);
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserAvatars = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

const UserAvatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid white;
  margin-right: -22px; // Creates overlapping effect
`;

const UserCount = styled.span`
  margin-left: 18px;
  font-weight: 600;
  color: #1c1c1f;
  font-size: 14px;
`;

export default Testimonials;
